import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Search, X } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import type { Option } from '@/constants/institute';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { cn } from '@/lib/utils';
import type { InstituteClass } from '@/types/instituteClass';

/** The shape every screen's filter state shares. */
export interface RosterFilterValues {
  search?: string;
  classUuid?: string;
  sectionUuid?: string;
  status?: string;
  /** The student's standing, on screens where `status` means something else. */
  studentStatus?: string;
  gender?: string;
  newThisYear?: string;
  sort?: string;
}

export interface RosterFiltersProps<T extends RosterFilterValues> {
  filters: T;
  classes: InstituteClass[];
  hasPlacement?: boolean;
  searchLabel: string;
  searchPlaceholder: string;
  statusLabel: string;
  statusOptions: readonly Option[];
  statusPlaceholder?: string;
  sortOptions: readonly Option[];
  onChange: (patch: Partial<T>) => void;
  hideBorder?: boolean;
  extraFilters?: { key: string; label: string; onRemove: () => void }[];
  onShowAll?: () => void;
  variant?: 'stacked' | 'bar';
  /** Selects of the page's own beside the standard ones — pass a fragment for several. */
  extraControls?: ReactNode;
}

/**
 * One dropdown for the narrowings a list's tiles open — gender or new
 * admissions, never both. Exported with its two translations so every filter
 * row that offers it (the register's, the fee summary's) reads and writes the
 * same pair of keys.
 */
export const ROSTER_CATEGORY_OPTIONS: Option[] = [
  { value: '', label: 'All students' },
  { value: 'BOYS', label: 'Boys' },
  { value: 'GIRLS', label: 'Girls' },
  { value: 'NEW_ADMISSIONS', label: 'New admissions' },
];

/** Which `ROSTER_CATEGORY_OPTIONS` entry a set of filters amounts to. */
export function rosterCategoryOf(filters: Pick<RosterFilterValues, 'gender' | 'newThisYear'>): string {
  if (filters.gender === 'MALE') return 'BOYS';
  if (filters.gender === 'FEMALE') return 'GIRLS';
  if (filters.newThisYear === 'true') return 'NEW_ADMISSIONS';
  return '';
}

/** The filter keys a category choice sets — each clears the other. */
export function rosterCategoryPatch(value: string): {
  gender: 'MALE' | 'FEMALE' | '';
  newThisYear: 'true' | '';
} {
  if (value === 'BOYS') return { gender: 'MALE', newThisYear: '' };
  if (value === 'GIRLS') return { gender: 'FEMALE', newThisYear: '' };
  if (value === 'NEW_ADMISSIONS') return { gender: '', newThisYear: 'true' };
  return { gender: '', newThisYear: '' };
}

/** The compact control shape every filter row uses, so the rows line up. */
export const FIELD_SHAPE_CLASS =
  '!rounded-xl !h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors';

export function RosterFilters<T extends RosterFilterValues>({
  filters,
  classes,
  hasPlacement = true,
  searchLabel,
  searchPlaceholder,
  statusLabel,
  statusOptions,
  statusPlaceholder = 'All',
  sortOptions,
  onChange,
  hideBorder: _hideBorder = false,
  extraFilters = [],
  onShowAll,
  extraControls,
}: RosterFiltersProps<T>) {
  const [searchDraft, setSearchDraft] = useState(filters.search ?? '');

  useEffect(() => {
    setSearchDraft(filters.search ?? '');
  }, [filters.search]);

  // The search runs as somebody types, once they pause — the box has no
  // button, and waiting for Enter read as a search that did nothing.
  const settledSearch = useDebouncedValue(searchDraft.trim(), 300);
  useEffect(() => {
    if (settledSearch !== (filters.search ?? '')) onChange({ search: settledSearch } as Partial<T>);
    // Only a settled draft should send a search; `filters.search` catching up is not one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settledSearch]);

  const selectedClass = classes.find((instituteClass) => instituteClass.uuid === filters.classUuid);

  const classOptions: Option[] = classes.map((instituteClass) => ({
    value: instituteClass.uuid,
    label: instituteClass.name,
  }));

  const sectionOptions: Option[] = (selectedClass?.sections ?? []).map((section) => ({
    value: section.uuid,
    label: `${section.name} — ${section.maxSeats} seats`,
  }));

  const patch = (values: Partial<Record<keyof RosterFilterValues, string>>) =>
    onChange(values as Partial<T>);

  function submitSearch(event: FormEvent) {
    event.preventDefault();
    patch({ search: searchDraft.trim() });
  }

  const currentCategory = rosterCategoryOf(filters);

  // No Graduated here: on the student roll the Status dropdown already lists
  // Alumni, and on the fee register it is not a fee question at all.
  function handleCategoryChange(value: string) {
    patch(rosterCategoryPatch(value));
  }

  const applied: { key: string; label: string; remove: () => void }[] = extraFilters.map(
    (filter) => ({ key: filter.key, label: filter.label, remove: filter.onRemove }),
  );

  if (filters.search) {
    applied.push({
      key: 'search',
      label: `Search: “${filters.search}”`,
      remove: () => patch({ search: '' }),
    });
  }
  if (selectedClass) {
    applied.push({
      key: 'class',
      label: `Class: ${selectedClass.name}`,
      remove: () => patch({ classUuid: '', sectionUuid: '' }),
    });
  }
  if (filters.sectionUuid) {
    const section = selectedClass?.sections.find((one) => one.uuid === filters.sectionUuid);
    applied.push({
      key: 'section',
      label: `Section: ${section?.name ?? '—'}`,
      remove: () => patch({ sectionUuid: '' }),
    });
  }
  if (filters.gender) {
    applied.push({
      key: 'gender',
      label: `Gender: ${filters.gender === 'MALE' ? 'Boys' : 'Girls'}`,
      remove: () => patch({ gender: '' }),
    });
  }
  if (filters.newThisYear) {
    applied.push({
      key: 'newThisYear',
      label: 'New admissions',
      remove: () => patch({ newThisYear: '' }),
    });
  }
  if (filters.status) {
    const chosen = statusOptions.find((option) => option.value === filters.status);
    applied.push({
      key: 'status',
      label: `${statusLabel}: ${chosen?.label ?? filters.status}`,
      remove: () => patch({ status: '' }),
    });
  }

  const showAll =
    onShowAll ??
    (() =>
      patch({
        search: '',
        classUuid: '',
        sectionUuid: '',
        status: '',
        studentStatus: '',
        gender: '',
        newThisYear: '',
      }));

  return (
    <section
      aria-label="Filter roster"
      className={cn(
        'rounded-2xl p-2.5 bg-slate-50 dark:bg-[#0b1322] border border-slate-200/90 dark:border-slate-800 shadow-xs flex flex-col gap-2.5',
      )}
    >
      <div className="flex flex-wrap items-end gap-2 w-full">
        {/* Searchbar taking the remaining entire width on the left */}
        <form onSubmit={submitSearch} className="w-full sm:w-auto sm:flex-1 min-w-0 sm:min-w-[200px]">
          <Input
            label={searchLabel}
            labelClassName="text-xs font-bold text-slate-700 dark:text-slate-200"
            placeholder={searchPlaceholder}
            type="search"
            autoComplete="off"
            className="!h-9 !py-1 !pl-9 !pr-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors w-full"
            leftIcon={<Search className="h-3.5 w-3.5 text-slate-400" />}
            value={searchDraft}
            onChange={(event) => {
              const val = event.target.value;
              setSearchDraft(val);
              if (val === '') {
                patch({ search: '' });
              }
            }}
          />
        </form>

        {/* Clean dropdown filters on the right side in the same single row */}
        <div className="flex flex-wrap items-end gap-2 w-full sm:w-auto shrink-0">
          {hasPlacement && (
            <>
              <div className="w-[calc(50%-0.25rem)] sm:w-32 shrink-0">
                <Select
                  label="Class"
                  labelClassName="text-xs font-bold text-slate-700 dark:text-slate-200"
                  options={classOptions}
                  placeholder="All classes"
                  className={FIELD_SHAPE_CLASS}
                  value={filters.classUuid ?? ''}
                  onChange={(event) => patch({ classUuid: event.target.value, sectionUuid: '' })}
                />
              </div>
              <div className="w-[calc(50%-0.25rem)] sm:w-32 shrink-0">
                <Select
                  label="Section"
                  labelClassName="text-xs font-bold text-slate-700 dark:text-slate-200"
                  options={sectionOptions}
                  placeholder={selectedClass ? 'All sections' : 'Select class'}
                  disabled={!selectedClass}
                  className={FIELD_SHAPE_CLASS}
                  value={filters.sectionUuid ?? ''}
                  onChange={(event) => patch({ sectionUuid: event.target.value })}
                />
              </div>
            </>
          )}

          <div className="w-full sm:w-44 shrink-0">
            <Select
              label="Sort by"
              labelClassName="text-xs font-bold text-slate-700 dark:text-slate-200"
              options={sortOptions}
              className={FIELD_SHAPE_CLASS}
              value={filters.sort ?? ''}
              onChange={(event) => patch({ sort: event.target.value })}
            />
          </div>

          <div className="w-[calc(50%-0.25rem)] sm:w-32 shrink-0">
            <Select
              label={statusLabel}
              labelClassName="text-xs font-bold text-slate-700 dark:text-slate-200"
              options={statusOptions}
              placeholder={statusPlaceholder}
              className={FIELD_SHAPE_CLASS}
              value={filters.status ?? ''}
              onChange={(event) => patch({ status: event.target.value })}
            />
          </div>

          <div className="w-[calc(50%-0.25rem)] sm:w-32 shrink-0">
            <Select
              label="Filter by"
              labelClassName="text-xs font-bold text-slate-700 dark:text-slate-200"
              options={ROSTER_CATEGORY_OPTIONS}
              className={FIELD_SHAPE_CLASS}
              value={currentCategory}
              onChange={(event) => handleCategoryChange(event.target.value)}
            />
          </div>

          {/* One control or several, each the width of the selects beside them. */}
          {extraControls && (
            <div className="flex w-full shrink-0 flex-col gap-3 sm:w-auto sm:flex-row sm:[&>*]:w-36">
              {extraControls}
            </div>
          )}
        </div>
      </div>

      {applied.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-200/80 dark:border-slate-800 pt-2.5">
          <span className="text-xs font-bold text-slate-500 dark:text-slate-400 shrink-0">Showing only</span>

          {applied.map((filter) => (
            <span
              key={filter.key}
              className="inline-flex items-center gap-1 rounded-xl border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-[#111c2e] py-1 pl-2.5 pr-1 text-xs font-bold text-slate-700 dark:text-slate-200 shadow-2xs"
            >
              {filter.label}
              <button
                type="button"
                aria-label={`Remove filter — ${filter.label}`}
                onClick={filter.remove}
                className="grid h-5 w-5 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-rose-500 focus:outline-none"
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </span>
          ))}

          <Button
            variant="ghost"
            size="sm"
            className="ml-auto !h-9 !py-1 !px-3 !text-[13px] font-bold border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-[#111c2e] text-slate-700 dark:text-slate-200 hover:bg-slate-50 hover:text-rose-600 hover:border-rose-300 shadow-2xs rounded-xl flex items-center gap-1.5 shrink-0 transition-colors"
            leftIcon={<X className="h-3.5 w-3.5" />}
            onClick={showAll}
          >
            Show all
          </Button>
        </div>
      )}
    </section>
  );
}
